import React, { useState, useMemo } from 'react';
import { Box, Button, Code, CopyButton, Group, NumberInput, Stack, Text } from '@mantine/core';
import { IconCheck, IconCode, IconCopy } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

interface EmbedCodeBlockProps {
  /** 分享页 URL，如 https://gbase.ai/bot/xxx */
  shareLink: string;
  /** 嵌入脚本的 base host，share-embed.js 的来源域 */
  baseHost: string;
  /** 紧凑布局（Popover 内使用），输入字段竖排 */
  compact?: boolean;
  /** 是否显示标题/说明（Popover 内可能不需要重复标题） */
  showHeader?: boolean;
}

/**
 * 分享页 iframe 嵌入代码生成器
 *
 * 输出包含：
 *   <iframe src=... allow="microphone; autoplay" data-gbase-max-height=...></iframe>
 *   <script src="{baseHost}/plugin/share-embed.js" defer></script>
 *
 * 详见 .features/chat-widget/docs/design-share-iframe-embed.md
 */
export const EmbedCodeBlock: React.FC<EmbedCodeBlockProps> = ({
  shareLink,
  baseHost,
  compact = false,
  showHeader = true,
}) => {
  const { t } = useTranslation();

  const [initHeight, setInitHeight] = useState<number>(600);
  // maxHeight 默认为空（不限制高度，iframe 保持 initHeight）
  const [maxHeight, setMaxHeight] = useState<number | ''>('');

  const hasMaxHeight = typeof maxHeight === 'number' && maxHeight > 0;

  const heightInvalid = useMemo(() => {
    if (initHeight < 100 || initHeight > 4000) return true;
    if (!hasMaxHeight) return false;
    if (maxHeight < 100 || maxHeight > 4000) return true;
    if (maxHeight < initHeight) return true;
    return false;
  }, [initHeight, maxHeight, hasMaxHeight]);

  const code = useMemo(() => {
    const maxHeightAttr = hasMaxHeight ? `\n  data-gbase-max-height="${maxHeight}"` : '';
    return `<iframe
  src="${shareLink}"
  style="width:100%;height:${initHeight}px;border:0"
  allow="microphone; autoplay"${maxHeightAttr}>
</iframe>
<script src="${baseHost}/plugin/share-embed.js" defer></script>`;
  }, [shareLink, baseHost, initHeight, maxHeight, hasMaxHeight]);

  const InputGroup = compact ? Stack : Group;
  const inputGroupProps = compact ? { gap: 8 } : { grow: true, gap: 12, align: 'flex-start' as const };

  // 两个 description 文案行数不同会导致输入框错位
  // 非紧凑模式下统一 description 区域最小高度，保证 label 顶部对齐 + 输入框底部对齐
  const numberInputStyles = compact ? undefined : { description: { minHeight: 36 } };

  return (
    <Box>
      {showHeader && (
        <Group gap={8} mb={12} align="center">
          <IconCode size={20} color="#374151" />
          <Text fw={600} fz={compact ? 14 : 16}>
            {t('shareSetting.embed.title', '嵌入到网站')}
          </Text>
        </Group>
      )}
      <Text fz={12} c="#8B95A1" mb={12} style={{ lineHeight: 1.5 }}>
        {t('shareSetting.embed.desc', '将代码粘贴到你网站的页面中即可嵌入分享页。')}
      </Text>
      <InputGroup {...inputGroupProps} mb={12}>
        <NumberInput
          label={t('shareSetting.embed.initHeight', '初始高度 (px)')}
          description={t('shareSetting.embed.initHeightDesc', '访客刚打开页面时显示的高度')}
          value={initHeight}
          onChange={(v) => setInitHeight(typeof v === 'number' ? v : 600)}
          min={100}
          max={4000}
          step={50}
          size={compact ? 'xs' : 'sm'}
          styles={numberInputStyles}
        />
        <NumberInput
          label={t('shareSetting.embed.maxHeight', '最大高度 (px)')}
          description={t('shareSetting.embed.maxHeightDesc', '用户开始对话后展开到的高度，留空则保持初始高度')}
          value={maxHeight}
          onChange={(v) => setMaxHeight(typeof v === 'number' ? v : '')}
          min={100}
          max={4000}
          step={50}
          size={compact ? 'xs' : 'sm'}
          placeholder={t('shareSetting.embed.maxHeightPlaceholder', '留空表示不限制')}
          styles={numberInputStyles}
          error={
            hasMaxHeight && maxHeight < initHeight
              ? t('shareSetting.embed.maxHeightError', '最大高度不能小于初始高度')
              : undefined
          }
        />
      </InputGroup>
      <Box style={{ position: 'relative' }}>
        <Code
          block
          style={{
            fontSize: compact ? 11 : 12,
            padding: compact ? 12 : 16,
            paddingRight: 44,
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            opacity: heightInvalid ? 0.5 : 1,
            maxHeight: compact ? 180 : undefined,
            overflow: compact ? 'auto' : undefined,
          }}
        >
          {code}
        </Code>
        <CopyButton value={code}>
          {({ copied, copy }) => (
            <Button
              size="xs"
              variant="light"
              color="gray"
              onClick={() => {
                if (heightInvalid) {
                  showNotification({
                    message: t('shareSetting.embed.invalidHeight', '请检查高度配置'),
                    color: 'red',
                  });
                  return;
                }
                copy();
                showNotification({
                  message: t('shareSetting.embed.copied', '已复制嵌入代码'),
                  color: 'green',
                });
              }}
              style={{
                position: 'absolute',
                right: 6,
                top: 6,
                padding: '4px 6px',
                backgroundColor: copied ? '#10B981' : '#F3F4F6',
                color: copied ? '#fff' : '#374151',
                minWidth: 'auto',
                height: 28,
              }}
            >
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </Button>
          )}
        </CopyButton>
      </Box>
    </Box>
  );
};

export default EmbedCodeBlock;
